class NeighborhoodGuide {
  constructor(container) {
    this.container = container;
    this.section = container;
    this.places = [];
    this.clustersLayer = null;
    this.mapState = {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isPanning: false,
      panStart: { x: 0, y: 0 },
      pinchStartDist: 0,
      lastScale: 1
    };
    this.mapInitialized = false;

    this.init();

    this.onResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.init(), 200);
    };
    window.addEventListener('resize', this.onResize);
  }

  preparePopups() {
    const popups = this.container.querySelectorAll('.neighborhood-guide__popup');
    popups.forEach(popup => {
      this.container.appendChild(popup);
    });
  }

  init() {
    const isMobile = window.innerWidth < 1024;
    if (isMobile === this.isMobile) {
      // Even if mode hasn't changed, we might need to re-center map on resize
      if (this.mapInitialized) this.setupAndCenterMap();
      return;
    }
    this.isMobile = isMobile;

    this.initFilters();
    this.initPopups();
    this.initShareButtons();

    if (this.isMobile) {
      this.initMobileLayout();
      this.initDrawer();
    } else {
      this.destroyMobileLayout();
    }

    this.initMap();
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    this.destroyMobileLayout();
    if (this.shareClickHandler) {
      this.container.removeEventListener('click', this.shareClickHandler);
    }
    // We should also clean up map listeners if we were thorough, but for now this is fine as the section is unloaded.
  }

  initMobileLayout() {
    document.body.classList.add('body-no-scroll--neighborhood');
    const siteHeader = document.querySelector('#shopify-section-header');
    const sectionHeading = this.container.querySelector('.neighborhood-guide__heading');
    const toolbar = this.container.querySelector('.neighborhood-guide__counters-toolbar');
    const headerHeight = siteHeader ? siteHeader.offsetHeight : 0;
    const headingHeight = sectionHeading ? sectionHeading.offsetHeight : 0;
    const toolbarHeight = toolbar ? toolbar.offsetHeight : 0;

    // Use window.innerHeight for accurate viewport height on mobile
    const viewportHeight = window.innerHeight;
    const mapHeight = viewportHeight - headingHeight - toolbarHeight;

    this.container.style.setProperty('--header-height', `${headerHeight}px`);
    this.container.style.setProperty('--heading-height', `${headingHeight}px`);
    this.container.style.setProperty('--toolbar-height', `${toolbarHeight}px`);
    this.container.style.setProperty('--map-height', `${mapHeight}px`);
    this.preparePopups();
  }

  destroyMobileLayout() {
    document.body.classList.remove('body-no-scroll--neighborhood');
  }

  initFilters() {
    const filterButtons = this.container.querySelectorAll('.neighborhood-guide__filter-button');
    if (!filterButtons.length) return;

    if (this.filterClickHandler) {
      filterButtons.forEach(button => button.removeEventListener('click', this.filterClickHandler));
    }

    this.filterClickHandler = (e) => {
      const button = e.currentTarget;
      const type = button.dataset.type;
      const currentType = this.section.dataset.activeType;
      const counters = this.container.querySelectorAll('.counter');
      const placeCardWrappers = this.container.querySelectorAll('.place-card-wrapper');

      if (currentType === type) {
        this.section.removeAttribute('data-active-type');
        button.classList.remove('active');
        this.updateCounters('total', counters);
      } else {
        this.section.dataset.activeType = type;
        filterButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
        this.updateCounters(type, counters);
      }
      this.filterPlaceCards(placeCardWrappers);
      this.updateClusters();
    };

    filterButtons.forEach(button => {
      button.addEventListener('click', this.filterClickHandler);
    });
  }

  updateCounters(activeType, counters) {
    counters.forEach(counter => {
      counter.style.display = (counter.dataset.type === activeType) ? 'flex' : 'none';
    });
  }

  filterPlaceCards(placeCardWrappers) {
    this.clearVisiblePlaceLabel();
    const activeType = this.section.dataset.activeType;
    placeCardWrappers.forEach(wrapper => {
      wrapper.style.display = (!activeType || wrapper.dataset.type === activeType) ? 'grid' : 'none';
    });
  }

  clearVisiblePlaceLabel() {
    this.container.querySelectorAll('[data-popup-trigger][data-label-visible="true"]').forEach(trigger => {
      trigger.removeAttribute('data-label-visible');
    });
  }

  showPlaceLabel(trigger) {
    this.clearVisiblePlaceLabel();
    trigger.dataset.labelVisible = 'true';
  }

  initPopups() {
    const triggers = this.container.querySelectorAll('[data-popup-trigger]');
    if (!triggers.length) return;

    if (this.popupTriggerClickHandler) {
      triggers.forEach(trigger => trigger.removeEventListener('click', this.popupTriggerClickHandler));
    }

    const closeAllPopups = () => {
      this.clearVisiblePlaceLabel();
      this.container.querySelectorAll('[data-popup-content]').forEach(p => {
        p.classList.remove('is-visible-mobile');
        p.dataset.visible = 'false';
        p.classList.remove('list-view-popup');
        p.classList.remove('popup-from-list-view');
      });
      this.container.classList.remove('mobile-popup-is-open');
    };

    this.popupTriggerClickHandler = (e) => {
      const trigger = e.currentTarget;
      const popupId = trigger.dataset.popupTrigger;
      const popup = this.container.querySelector(`[data-popup-content="${popupId}"]`);
      if (!popup) return;

      if (window.innerWidth >= 1024 && popup.contains(e.target)) {
        return;
      }

      const isMobile = window.innerWidth < 1024;
      const wasVisible = isMobile
        ? popup.classList.contains('is-visible-mobile')
        : popup.dataset.visible === 'true';

      if (isMobile && !wasVisible && trigger.dataset.labelVisible !== 'true') {
        this.showPlaceLabel(trigger);
        e.stopPropagation();
        return;
      }

      if (!wasVisible) {
        closeAllPopups();
      } else {
        closeAllPopups();
        e.stopPropagation();
        return;
      }

      if (!wasVisible) {
        if (isMobile) {
          popup.classList.add('is-visible-mobile');
          this.container.classList.add('mobile-popup-is-open');

          if (trigger.classList.contains('mobile-list-view-button')) {
            popup.classList.add('list-view-popup');
            popup.classList.add('popup-from-list-view');
          }
        } else {
          popup.dataset.visible = 'true';
        }
      }

      e.stopPropagation();
    };

    triggers.forEach(trigger => {
      trigger.addEventListener('click', this.popupTriggerClickHandler);
    });

    this.container.querySelectorAll('.neighborhood-guide__close-popup').forEach(button => {
      button.addEventListener('click', (e) => {
        closeAllPopups();
        e.stopPropagation();
      });
    });

    this.container.querySelectorAll('[data-popup-content]').forEach(popup => {
      popup.addEventListener('click', (e) => {
        if (e.target.closest('.place-card__share-button')) {
          return;
        }
        e.stopPropagation();
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-popup-trigger]') && !e.target.closest('[data-popup-content]')) {
        closeAllPopups();
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    const placeId = urlParams.get('place');
    if (placeId) {
      const trigger = this.container.querySelector(`[data-popup-trigger="${placeId}"]`);
      if (trigger) {
        setTimeout(() => {
          trigger.click();
          if (window.innerWidth >= 1024) {
            const mapContainer = this.container.querySelector('.neighborhood-guide__map-container');
            if (mapContainer) {
              mapContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
          const newUrl = new URL(window.location);
          newUrl.searchParams.delete('place');
          window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
        }, 500);
      }
    }
  }

  initShareButtons() {
    if (this.shareClickHandler) {
      this.container.removeEventListener('click', this.shareClickHandler);
    }

    this.shareClickHandler = (e) => {
      const button = e.target.closest('.place-card__share-button');
      if (!button) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const urlToCopy = button.dataset.shareUrl;
      if (!urlToCopy) return;

      navigator.clipboard.writeText(urlToCopy).then(() => {
        button.classList.add('is-copied');
        this.showNotification(button, 'Link copied');
        setTimeout(() => {
          button.classList.remove('is-copied');
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy URL: ', err);
        button.classList.add('is-failed');
        setTimeout(() => {
          button.classList.remove('is-failed');
        }, 2000);
      });
    };

    this.container.addEventListener('click', this.shareClickHandler);
  }

  initDrawer() {
    const drawer = this.container.querySelector('.neighborhood-guide-drawer');
    const toolbar = this.container.querySelector('.neighborhood-guide__counters-toolbar');
    if (!drawer || !toolbar) return;

    this.drawerClickHandler = () => drawer.classList.toggle('is-open');
    toolbar.addEventListener('click', this.drawerClickHandler);
  }

  initMap() {
    if (this.mapInitialized) {
      this.setupAndCenterMap();
      this.onScaleChanged();
      this.updateClusters();
      return;
    }

    this.mapInitialized = true;
    this.setupAndCenterMap();
    this.collectPlaces();
    this.initClustersLayer();
    this.onScaleChanged();
    this.attachMapListeners();
  }

  collectPlaces() {
    const mapWrapper = this.container.querySelector('.map-wrapper');
    if (!mapWrapper) return;

    const placePins = this.container.querySelectorAll('.neighborhood-guide__place-pin');
    this.places = [];

    placePins.forEach(pin => {
      // Hide pins initially to avoid flash of unclustered markers before clustering is applied
      pin.style.visibility = 'hidden';

      const place = pin.querySelector('.neighborhood-guide__place');
      if (!place) return;

      const type = place.dataset.type;
      const isOverground = type === 'tram_station';

      const topPercent = parseFloat(pin.style.top);
      const leftPercent = parseFloat(pin.style.left);
      if (isNaN(topPercent) || isNaN(leftPercent)) return;

      this.places.push({
        pin,
        place,
        type,
        isOverground,
        topPercent,
        leftPercent
      });
    });
  }

  initClustersLayer() {
    const mapWrapper = this.container.querySelector('.map-wrapper');
    if (!mapWrapper) return;

    let layer = mapWrapper.querySelector('.neighborhood-guide__clusters-layer');
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'neighborhood-guide__clusters-layer';
      mapWrapper.appendChild(layer);
    }
    this.clustersLayer = layer;
  }

  applyMapTransform() {
    const mapContainer = this.container.querySelector('.neighborhood-guide__map-container');
    const mapWrapper = this.container.querySelector('.map-wrapper');
    if (!mapContainer || !mapWrapper) return;

    const { scale } = this.mapState;
    let { translateX, translateY } = this.mapState;

    const wrapperWidth = parseFloat(mapWrapper.style.width) * scale;
    const wrapperHeight = parseFloat(mapWrapper.style.height) * scale;
    const containerWidth = mapContainer.offsetWidth;
    const containerHeight = mapContainer.offsetHeight;

    const minX = containerWidth - wrapperWidth;
    const minY = containerHeight - wrapperHeight;

    if (wrapperWidth < containerWidth) {
      translateX = (containerWidth - wrapperWidth) / 2;
    } else {
      translateX = Math.max(minX, Math.min(0, translateX));
    }

    if (wrapperHeight < containerHeight) {
      translateY = (containerHeight - wrapperHeight) / 2;
    } else {
      translateY = Math.max(minY, Math.min(0, translateY));
    }

    this.mapState.translateX = translateX;
    this.mapState.translateY = translateY;

    mapWrapper.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;

    // Apply inverse scale to pins to keep them the same size
    const pins = this.container.querySelectorAll(
      '.neighborhood-guide__place-pin, .neighborhood-guide__store-pin, .neighborhood-guide__cluster-pin'
    );
    const inverseScale = 1 / scale;
    pins.forEach(pin => {
      const isStore = pin.classList.contains('neighborhood-guide__store-pin');
      pin.style.transform = `translate(-50%, ${isStore ? '-100%' : '-50%'}) scale(${inverseScale})`;
    });
  }

  setupAndCenterMap() {
    const mapContainer = this.container.querySelector('.neighborhood-guide__map-container');
    const mapWrapper = this.container.querySelector('.map-wrapper');
    const mapImage = this.container.querySelector('.neighborhood-guide__map');

    if (!mapContainer || !mapWrapper || !mapImage) return;

    if (mapImage.naturalWidth === 0) {
      mapImage.addEventListener('load', () => this.setupAndCenterMap());
      return;
    }

    const containerHeight = mapContainer.offsetHeight;
    const containerWidth = mapContainer.offsetWidth;
    const imageAspectRatio = mapImage.naturalWidth / mapImage.naturalHeight;

    let wrapperHeight = containerHeight;
    let wrapperWidth = wrapperHeight * imageAspectRatio;

    if (wrapperWidth < containerWidth) {
      wrapperWidth = containerWidth;
      wrapperHeight = wrapperWidth / imageAspectRatio;
    }

    mapWrapper.style.height = `${wrapperHeight}px`;
    mapWrapper.style.width = `${wrapperWidth}px`;
    mapWrapper.style.transformOrigin = '0 0';

    // Get default zoom from data attributes
    const isMobile = window.innerWidth < 1024;
    const defaultZoomAttr = isMobile ? 'defaultZoomMobile' : 'defaultZoomDesktop';
    const defaultZoom = parseFloat(this.container.dataset[defaultZoomAttr]) || 1.5;
    console.log(4588545948)
    console.log(defaultZoom)

    this.mapState.scale = defaultZoom;
    this.mapState.translateX = (containerWidth - wrapperWidth * defaultZoom) / 2;
    this.mapState.translateY = (containerHeight - wrapperHeight * defaultZoom) / 2;

    // Check for URL parameter 'place' and center map if present
    const urlParams = new URLSearchParams(window.location.search);
    const placeId = urlParams.get('place');
    let centeredOnPlace = false;

    if (placeId) {
      const placePin = this.container.querySelector(`.neighborhood-guide__place[data-block-id="${placeId}"]`);
      if (placePin) {
        const pinWrapper = placePin.closest('.neighborhood-guide__place-pin');
        if (pinWrapper) {
          const topPercent = parseFloat(pinWrapper.style.top);
          const leftPercent = parseFloat(pinWrapper.style.left);

          if (!isNaN(topPercent) && !isNaN(leftPercent)) {
            const pinX = wrapperWidth * (leftPercent / 100);
            const pinY = wrapperHeight * (topPercent / 100);

            this.mapState.translateX = containerWidth / 2 - pinX * defaultZoom;
            this.mapState.translateY = containerHeight / 2 - pinY * defaultZoom;
            centeredOnPlace = true;
          }
        }
      }

      // Remove 'place' parameter from URL to keep it clean
      urlParams.delete('place');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }

    // If no place was found/selected, center on store pin
    if (!centeredOnPlace) {
      const storePin = this.container.querySelector('.neighborhood-guide__store-pin');
      if (storePin) {
        const topPercent = parseFloat(storePin.style.top);
        const leftPercent = parseFloat(storePin.style.left);

        if (!isNaN(topPercent) && !isNaN(leftPercent)) {
          const pinX = wrapperWidth * (leftPercent / 100);
          const pinY = wrapperHeight * (topPercent / 100);

          this.mapState.translateX = containerWidth / 2 - pinX * defaultZoom;
          this.mapState.translateY = containerHeight / 2 - pinY * defaultZoom;
        }
      }
    }

    this.applyMapTransform();
  }

  onScaleChanged() {
    // Recalculate clusters when zoom level changes
    this.updateClusters();
  }

  updateClusters() {
    if (!this.clustersLayer) this.initClustersLayer();
    if (!this.clustersLayer || !this.places.length) return;

    const scale = this.mapState.scale;

    // Always reset any previous cluster state
    this.clustersLayer.innerHTML = '';
    this.places.forEach(({ pin }) => {
      pin.style.display = '';
    });

    // Max zoom: no clustering, all points separate
    if (scale > 1.7) {
      // Ensure all visible pins are shown individually and made visible
      const activeType = this.section.dataset.activeType || null;
      this.places.forEach(({ pin, type }) => {
        if (activeType && type !== activeType) {
          pin.style.display = 'none';
        } else {
          pin.style.display = '';
        }
      });

      this.places.forEach(({ pin }) => {
        pin.style.visibility = pin.style.display === 'none' ? 'hidden' : 'visible';
      });

      return;
    }

    const mapWrapper = this.container.querySelector('.map-wrapper');
    if (!mapWrapper) return;

    const wrapperWidth = parseFloat(mapWrapper.style.width);
    const wrapperHeight = parseFloat(mapWrapper.style.height);
    if (!wrapperWidth || !wrapperHeight) return;

    const activeType = this.section.dataset.activeType || null;

    // Determine grid cell size based on zoom
    let cellSize;
    if (scale <= 1.3) {
      // Min zoom – heavy clustering
      cellSize = 220;
    } else {
      // Mid zoom – only overlapping/nearby points cluster
      cellSize = 120;
    }

    const clustersByKey = new Map();

    this.places.forEach(place => {
      const { pin, type, isOverground, topPercent, leftPercent } = place;

      // THE BOX / store marker and Overground markers never cluster
      if (pin.classList.contains('neighborhood-guide__store-pin') || isOverground) {
        return;
      }

      // Respect active type filter
      if (activeType && type !== activeType) {
        pin.style.display = 'none';
        return;
      } else {
        pin.style.display = '';
      }

      const x = wrapperWidth * (leftPercent / 100);
      const y = wrapperHeight * (topPercent / 100);

      const col = Math.floor(x / cellSize);
      const row = Math.floor(y / cellSize);
      const key = `${row}:${col}`;

      if (!clustersByKey.has(key)) {
        clustersByKey.set(key, {
          items: [],
          sumX: 0,
          sumY: 0
        });
      }

      const cluster = clustersByKey.get(key);
      cluster.items.push(place);
      cluster.sumX += x;
      cluster.sumY += y;
    });

    clustersByKey.forEach(cluster => {
      const count = cluster.items.length;

      if (count <= 1) {
        // Single item in cell – leave original pin visible
        return;
      }

      // For multi-item clusters, hide individual pins and show a cluster pin
      const centerX = cluster.sumX / count;
      const centerY = cluster.sumY / count;

      cluster.items.forEach(({ pin }) => {
        pin.style.display = 'none';
      });

      const clusterPin = this.createClusterPin(count, centerX, centerY, wrapperWidth, wrapperHeight);
      this.clustersLayer.appendChild(clusterPin);
    });

    // Finally, reveal pins that remain visible and keep hidden ones hidden,
    // so we don't see a flash of all markers during clustering updates.
    this.places.forEach(({ pin }) => {
      pin.style.visibility = pin.style.display === 'none' ? 'hidden' : 'visible';
    });

    // Ensure cluster pins get the same inverse scale as regular pins immediately,
    // so they don't jump in size only after the first pan/zoom.
    const inverseScale = 1 / scale;
    this.clustersLayer
      .querySelectorAll('.neighborhood-guide__cluster-pin')
      .forEach(pin => {
        pin.style.transform = `translate(-50%, -50%) scale(${inverseScale})`;
      });
  }

  createClusterPin(count, centerX, centerY, wrapperWidth, wrapperHeight) {
    const cluster = document.createElement('div');
    cluster.className = 'neighborhood-guide__cluster-pin';

    const leftPercent = (centerX / wrapperWidth) * 100;
    const topPercent = (centerY / wrapperHeight) * 100;

    cluster.style.position = 'absolute';
    cluster.style.left = `${leftPercent}%`;
    cluster.style.top = `${topPercent}%`;

    let sizeClass = 'cluster--small';
    if (count > 30) {
      sizeClass = 'cluster--large';
    } else if (count > 10) {
      sizeClass = 'cluster--medium';
    }

    cluster.classList.add(sizeClass);
    cluster.dataset.count = String(count);

    // Basic cluster icon with count – styling handled in CSS
    cluster.innerHTML = `<div class="neighborhood-guide__cluster-icon"><span class="neighborhood-guide__cluster-count">${count}</span></div>`;

    cluster.addEventListener('click', (e) => {
      e.stopPropagation();
      this.zoomIntoCluster(centerX, centerY);
    });

    return cluster;
  }

  zoomIntoCluster(centerX, centerY) {
    const mapContainer = this.container.querySelector('.neighborhood-guide__map-container');
    const mapWrapper = this.container.querySelector('.map-wrapper');
    if (!mapContainer || !mapWrapper) return;

    const containerWidth = mapContainer.offsetWidth;
    const containerHeight = mapContainer.offsetHeight;

    let targetScale;
    const currentScale = this.mapState.scale;

    if (currentScale <= 1.3) {
      // Min -> mid zoom
      targetScale = 1.5;
    } else {
      // Mid -> max zoom
      targetScale = 1.9;
    }

    targetScale = Math.max(1, Math.min(targetScale, 2));

    this.mapState.scale = targetScale;
    this.mapState.translateX = containerWidth / 2 - centerX * targetScale;
    this.mapState.translateY = containerHeight / 2 - centerY * targetScale;

    this.applyMapTransform();
    this.onScaleChanged();
  }

  attachMapListeners() {
    const mapContainer = this.container.querySelector('.neighborhood-guide__map-container');
    const mapImage = this.container.querySelector('.neighborhood-guide__map');
    if (!mapContainer || !mapImage) return;

    // Touch Events
    mapContainer.addEventListener('touchstart', (e) => {
      if (e.target.closest('.neighborhood-guide__filters')) return;

      if (e.touches.length === 1) {
        this.mapState.isPanning = true;
        this.mapState.panStart.x = e.touches[0].clientX - this.mapState.translateX;
        this.mapState.panStart.y = e.touches[0].clientY - this.mapState.translateY;
      } else if (e.touches.length === 2) {
        this.mapState.isPanning = false;
        const t1 = e.touches[0], t2 = e.touches[1];
        this.mapState.pinchStartDist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        this.mapState.lastScale = this.mapState.scale;
      }
    });

    mapContainer.addEventListener('touchmove', (e) => {
      if (e.target.closest('.neighborhood-guide__filters')) return;

      e.preventDefault();
      if (e.touches.length === 1 && this.mapState.isPanning) {
        this.mapState.translateX = e.touches[0].clientX - this.mapState.panStart.x;
        this.mapState.translateY = e.touches[0].clientY - this.mapState.panStart.y;
        this.applyMapTransform();
      } else if (e.touches.length === 2) {
        const t1 = e.touches[0], t2 = e.touches[1];
        const currentDist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        if (this.mapState.pinchStartDist === 0) return;

        const newScale = (currentDist / this.mapState.pinchStartDist) * this.mapState.lastScale;
        const clampedScale = Math.max(1, Math.min(newScale, 2));

        const midX = (t1.clientX + t2.clientX) / 2;
        const midY = (t1.clientY + t2.clientY) / 2;
        const rect = mapContainer.getBoundingClientRect();
        const zoomPointX = midX - rect.left;
        const zoomPointY = midY - rect.top;

        this.mapState.translateX = zoomPointX - (zoomPointX - this.mapState.translateX) * (clampedScale / this.mapState.scale);
        this.mapState.translateY = zoomPointY - (zoomPointY - this.mapState.translateY) * (clampedScale / this.mapState.scale);
        this.mapState.scale = clampedScale;

        this.applyMapTransform();
        this.onScaleChanged();
      }
    }, { passive: false });

    mapContainer.addEventListener('touchend', (e) => {
      if (e.touches.length === 0) this.mapState.isPanning = false;
      else if (e.touches.length === 1) {
        this.mapState.isPanning = true;
        this.mapState.panStart.x = e.touches[0].clientX - this.mapState.translateX;
        this.mapState.panStart.y = e.touches[0].clientY - this.mapState.translateY;
      }
      this.mapState.lastScale = this.mapState.scale;
    });

    // Mouse Events for Desktop
    mapContainer.addEventListener('mousedown', (e) => {
      if (e.target.closest('.neighborhood-guide__filters')) return;

      this.mapState.isPanning = true;
      this.mapState.panStart.x = e.clientX - this.mapState.translateX;
      this.mapState.panStart.y = e.clientY - this.mapState.translateY;
      mapContainer.style.cursor = 'grabbing';
      e.preventDefault(); // Prevent potential text selection or other default behaviors
    });

    // Prevent default image dragging
    mapImage.addEventListener('dragstart', (e) => {
      e.preventDefault();
    });

    window.addEventListener('mousemove', (e) => {
      if (!this.mapState.isPanning) return;
      e.preventDefault();
      this.mapState.translateX = e.clientX - this.mapState.panStart.x;
      this.mapState.translateY = e.clientY - this.mapState.panStart.y;
      this.applyMapTransform();
    });

    window.addEventListener('mouseup', () => {
      this.mapState.isPanning = false;
      mapContainer.style.cursor = 'grab';
    });

    // Wheel Zoom
    mapContainer.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = -Math.sign(e.deltaY) * 0.1;
      const newScale = Math.max(1, Math.min(this.mapState.scale + delta, 2));

      const rect = mapContainer.getBoundingClientRect();
      const zoomPointX = e.clientX - rect.left;
      const zoomPointY = e.clientY - rect.top;

      this.mapState.translateX = zoomPointX - (zoomPointX - this.mapState.translateX) * (newScale / this.mapState.scale);
      this.mapState.translateY = zoomPointY - (zoomPointY - this.mapState.translateY) * (newScale / this.mapState.scale);

      this.mapState.scale = newScale;
      this.applyMapTransform();
      this.onScaleChanged();
    }, { passive: false });
  }

  showNotification(button, message) {
    // Remove existing notification if present
    const existingNotification = button.parentNode.querySelector('.share-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement('div');
    notification.className = 'share-notification';
    notification.textContent = message;

    // Append to the button's parent (actions container)
    button.parentNode.appendChild(notification);

    // Force reflow
    notification.offsetHeight;

    notification.classList.add('is-visible');

    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => {
        notification.remove();
      }, 300); // Wait for transition to finish
    }, 2000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const guideSections = document.querySelectorAll('[data-section-type="neighborhood-guide"]');
  guideSections.forEach(section => {
    if (!section.matches('.js-initialized')) {
      section.neighborhoodGuideInstance = new NeighborhoodGuide(section);
      section.classList.add('js-initialized');
    }
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('[data-section-type="neighborhood-guide"]');
  if (section && !section.matches('.js-initialized')) {
    section.neighborhoodGuideInstance = new NeighborhoodGuide(section);
    section.classList.add('js-initialized');
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('[data-section-type="neighborhood-guide"]');
  if (section && section.neighborhoodGuideInstance) {
    section.neighborhoodGuideInstance.destroy();
    delete section.neighborhoodGuideInstance;
  }
});
